import { useState } from 'preact/hooks';
import { t, tError, lang, setLang, LANGUAGES, type Lang } from '../lib/i18n.js';
import { textSize, setTextSize, type TextSize } from '../lib/prefs.js';
import * as api from '../lib/api.js';
import { ApiError } from '../lib/api.js';
import {
  appLockEnabled,
  hasBiometricSupport,
  hasBiometricEnrolled,
  hasPin,
  setPin as saveAppLockPin,
  registerBiometric,
  disableAppLock,
} from '../lib/applock.js';

const TEXT_SIZES: TextSize[] = ['condensed', 'medium', 'large'];

export function Settings({
  accessToken,
  displayName,
  onBack,
}: {
  accessToken: string;
  displayName: string;
  onBack: () => void;
}) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwMessage, setPwMessage] = useState<{ text: string; ok: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [lockMessage, setLockMessage] = useState<{ text: string; ok: boolean } | null>(null);

  async function submitPinSetup(e: Event) {
    e.preventDefault();
    setLockMessage(null);
    if (newPin.length !== 4) {
      setLockMessage({ text: t('appLock.pinInvalid'), ok: false });
      return;
    }
    if (newPin !== confirmPin) {
      setLockMessage({ text: t('appLock.pinMismatch'), ok: false });
      return;
    }
    await saveAppLockPin(newPin);
    setNewPin('');
    setConfirmPin('');
    setLockMessage({ text: t('appLock.pinSet'), ok: true });
  }

  async function setupBiometric() {
    setLockMessage(null);
    const ok = await registerBiometric();
    setLockMessage(
      ok
        ? { text: t('appLock.biometricRegistered'), ok: true }
        : { text: t('appLock.biometricFailed'), ok: false },
    );
  }

  async function submitPasswordChange(e: Event) {
    e.preventDefault();
    setPwMessage(null);
    if (newPassword !== confirmPassword) {
      setPwMessage({ text: t('settings.passwordMismatch'), ok: false });
      return;
    }
    setBusy(true);
    try {
      await api.changePassword(accessToken, currentPassword, newPassword);
      setPwMessage({ text: t('settings.passwordChanged'), ok: true });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const msg =
        err instanceof ApiError ? tError(err.code, err.message) : t('err.generic');
      setPwMessage({ text: msg, ok: false });
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="settings-shell">
      <header className="settings-header">
        <button type="button" className="settings-back" onClick={onBack} aria-label={t('settings.back')}>
          ← {t('settings.back')}
        </button>
        <h1>{t('settings.title')}</h1>
      </header>

      <section className="settings-section">
        <h2>{t('settings.account')}</h2>
        <p className="settings-signed-in">{t('settings.signedInAs', { name: displayName })}</p>
      </section>

      <section className="settings-section">
        <h2>{t('settings.language')}</h2>
        <div className="settings-choices">
          {LANGUAGES.map(({ code, label, flag }) => (
            <button
              key={code}
              type="button"
              className={`settings-choice ${lang.value === code ? 'settings-choice--active' : ''}`}
              onClick={() => setLang(code as Lang)}
            >
              <span className="settings-choice__flag">{flag}</span>
              {label}
            </button>
          ))}
        </div>
        <p className="settings-hint">{t('settings.appliesInstantly')}</p>
      </section>

      <section className="settings-section">
        <h2>{t('settings.textSize')}</h2>
        <div className="settings-choices">
          {TEXT_SIZES.map((size) => (
            <button
              key={size}
              type="button"
              className={`settings-choice ${textSize.value === size ? 'settings-choice--active' : ''}`}
              onClick={() => setTextSize(size)}
            >
              {t(`settings.textSize.${size}`)}
            </button>
          ))}
        </div>
        <p className="settings-preview bubble" style={{ alignSelf: 'flex-start' }}>
          {t('app.tagline')}
        </p>
        <p className="settings-hint">{t('settings.appliesInstantly')}</p>
      </section>

      <section className="settings-section settings-card">
        <h2>{t('settings.changePassword')}</h2>
        <form className="auth-form" onSubmit={submitPasswordChange}>
          <label>
            {t('settings.currentPassword')}
            <input
              type="password"
              value={currentPassword}
              onInput={(e) => setCurrentPassword((e.target as HTMLInputElement).value)}
              autocomplete="current-password"
              required
            />
          </label>
          <label>
            {t('settings.newPassword')}
            <input
              type="password"
              value={newPassword}
              onInput={(e) => setNewPassword((e.target as HTMLInputElement).value)}
              autocomplete="new-password"
              minLength={10}
              required
            />
          </label>
          <label>
            {t('settings.confirmPassword')}
            <input
              type="password"
              value={confirmPassword}
              onInput={(e) => setConfirmPassword((e.target as HTMLInputElement).value)}
              autocomplete="new-password"
              required
            />
          </label>
          {pwMessage && (
            <p className={pwMessage.ok ? 'form-success' : 'form-error'}>{pwMessage.text}</p>
          )}
          <button type="submit" disabled={busy}>
            {busy ? t('auth.pleaseWait') : t('settings.save')}
          </button>
        </form>
      </section>

      <section className="settings-section settings-card">
        <h2>{t('appLock.title')}</h2>
        <p className="settings-hint">{t('appLock.explain')}</p>

        {appLockEnabled.value && (
          <p className="settings-signed-in">
            {hasPin() && t('appLock.pinIsSet')}
            {hasPin() && hasBiometricEnrolled() && ' · '}
            {hasBiometricEnrolled() && t('appLock.biometricIsSet')}
          </p>
        )}

        <form className="auth-form" onSubmit={submitPinSetup}>
          <label>
            {t('appLock.newPin')}
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              value={newPin}
              onInput={(e) => setNewPin((e.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 4))}
            />
          </label>
          <label>
            {t('appLock.confirmPin')}
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              value={confirmPin}
              onInput={(e) =>
                setConfirmPin((e.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 4))
              }
            />
          </label>
          {lockMessage && (
            <p className={lockMessage.ok ? 'form-success' : 'form-error'}>{lockMessage.text}</p>
          )}
          <button type="submit">{t('appLock.setPin')}</button>
          {hasBiometricSupport() && (
            <button type="button" onClick={setupBiometric}>
              {t('appLock.registerBiometric')}
            </button>
          )}
          {appLockEnabled.value && (
            <button
              type="button"
              onClick={() => {
                disableAppLock();
                setLockMessage(null);
              }}
            >
              {t('appLock.disable')}
            </button>
          )}
        </form>
      </section>
    </main>
  );
}
